import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import GiveawayCard from './components/GiveawayCard';
import GiveawayFilters from './components/GiveawayFilters';
import ParticipationModal from './components/ParticipationModal';
import MyEntriesSection from './components/MyEntriesSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { giveawaysService } from '../../services/giveaways';

const GiveawaysPage = () => {
  const [giveaways, setGiveaways] = useState([]);
  const [filteredGiveaways, setFilteredGiveaways] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    entryMethod: 'all',
    prizeValue: 'all',
    status: 'active',
    sortBy: 'ending'
  });
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [isParticipationModalOpen, setIsParticipationModalOpen] = useState(false);
  const [showMyEntries, setShowMyEntries] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isLoggedIn, setIsLoggedIn] = useState(true); // Mock auth state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEntries, setUserEntries] = useState([]);

  // Mock data for giveaways
  const mockGiveaways = [
    {
      id: 1,
      title: "iPhone 15 Pro Max Giveaway",
      description: "Win the latest iPhone 15 Pro Max with 256GB storage. Enter now for your chance to upgrade!",
      sponsor: "TechWorld",
      sponsorLogo: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100",
      coverImage: "https://images.unsplash.com/photo-1695048133142-1a20484d2569?w=600",
      prizeValue: "$1,199",
      category: "tech",
      entryMethod: "code",
      startDate: "2024-01-15",
      endDate: "2024-02-15",
      timeRemaining: "15 days",
      participants: 12847,
      requirements: ["Follow @TechWorld", "Enter code TECH2024"],
      eligibility: "18+ years old, US residents only",
      status: "active",
      featured: true
    },
    {
      id: 2,
      title: "Luxury Spa Weekend Retreat",
      description: "Enjoy a relaxing weekend at the Grand Spa Resort with all amenities included.",
      sponsor: "Wellness Co",
      sponsorLogo: "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=100",
      coverImage: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=600",
      prizeValue: "$800",
      category: "lifestyle",
      entryMethod: "social",
      startDate: "2024-01-20",
      endDate: "2024-02-20",
      timeRemaining: "20 days",
      participants: 5632,
      requirements: ["Share this post", "Tag 3 friends"],
      eligibility: "21+ years old, no location restrictions",
      status: "active",
      featured: false
    },
    {
      id: 3,
      title: "Gaming Setup Bundle",
      description: "Complete gaming setup including RGB keyboard, mouse, headset, and gaming chair.",
      sponsor: "GameTech",
      sponsorLogo: "https://images.unsplash.com/photo-1556438064-2d7646166914?w=100",
      coverImage: "https://images.unsplash.com/photo-1593305841991-05c297ba4575?w=600",
      prizeValue: "$1,500",
      category: "tech",
      entryMethod: "purchase",
      startDate: "2024-01-10",
      endDate: "2024-02-10",
      timeRemaining: "10 days",
      participants: 8934,
      requirements: ["Purchase any item", "Enter receipt code"],
      eligibility: "18+ years old, worldwide shipping",
      status: "active",
      featured: true
    },
    {
      id: 4,
      title: "Adventure Photography Experience",
      description: "Professional photography workshop in the Swiss Alps with renowned nature photographer.",
      sponsor: "Photo Adventures",
      sponsorLogo: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=100",
      coverImage: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600",
      prizeValue: "$2,500",
      category: "experiences",
      entryMethod: "code",
      startDate: "2024-01-05",
      endDate: "2024-02-05",
      timeRemaining: "5 days",
      participants: 3421,
      requirements: ["Enter code PHOTO2024"],
      eligibility: "18+ years old, valid passport required",
      status: "active",
      featured: false
    },
    {
      id: 5,
      title: "Home Theater System",
      description: "Premium 7.1 surround sound system with 4K projector and 120-inch screen.",
      sponsor: "AudioVision",
      sponsorLogo: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=100",
      coverImage: "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=600",
      prizeValue: "$3,200",
      category: "tech",
      entryMethod: "social",
      startDate: "2024-01-12",
      endDate: "2024-03-12",
      timeRemaining: "45 days",
      participants: 6789,
      requirements: ["Follow @AudioVision", "Share with friends"],
      eligibility: "21+ years old, US and Canada only",
      status: "active",
      featured: true
    },
    {
      id: 6,
      title: "Fitness Transformation Package",
      description: "3-month personal training program with nutrition coaching and premium gym membership.",
      sponsor: "FitLife",
      sponsorLogo: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=100",
      coverImage: "https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=600",
      prizeValue: "$2,000",
      category: "lifestyle",
      entryMethod: "code",
      startDate: "2024-01-18",
      endDate: "2024-02-18",
      timeRemaining: "18 days",
      participants: 4567,
      requirements: ["Enter code FITLIFE2024"],
      eligibility: "18+ years old, local gym access required",
      status: "active",
      featured: false
    }
  ];

  // Mock user entries
  const mockUserEntries = [
    {
      id: 1,
      giveawayId: 1,
      giveawayTitle: "iPhone 15 Pro Max Giveaway",
      entryDate: "2024-01-20",
      entryCode: "TECH2024",
      status: "confirmed",
      entryNumber: "#12848"
    },
    {
      id: 2,
      giveawayId: 4,
      giveawayTitle: "Adventure Photography Experience",
      entryDate: "2024-01-19",
      entryCode: "PHOTO2024",
      status: "confirmed",
      entryNumber: "#3422"
    },
    {
      id: 3,
      giveawayId: 6,
      giveawayTitle: "Fitness Transformation Package",
      entryDate: "2024-01-21",
      entryCode: "FITLIFE2024",
      status: "confirmed",
      entryNumber: "#4568"
    }
  ];

  // Load giveaways from API
  const loadGiveaways = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await giveawaysService.getGiveaways();
      setGiveaways(data.results || data);
    } catch (err) {
      setError('Failed to load giveaways. Please try again.');
      console.error('Error loading giveaways:', err);
      // Fallback to mock data if API fails
      setGiveaways(mockGiveaways);
    } finally {
      setLoading(false);
    }
  };

  // Load user entries
  const loadUserEntries = async () => {
    if (isLoggedIn) {
      try {
        const data = await giveawaysService.getMyEntries();
        setUserEntries(data.results || data);
      } catch (err) {
        console.error('Error loading user entries:', err);
        // Fallback to mock data if API fails
        setUserEntries(mockUserEntries);
      }
    }
  };

  useEffect(() => {
    loadGiveaways();
    loadUserEntries();
  }, [isLoggedIn]);

  useEffect(() => {
    let filtered = [...giveaways];

    // Apply search filter
    if (filters.search) {
      filtered = filtered.filter(giveaway =>
        giveaway.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        giveaway.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        giveaway.sponsor.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Apply category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(giveaway => giveaway.category === filters.category);
    }

    // Apply entry method filter
    if (filters.entryMethod !== 'all') {
      filtered = filtered.filter(giveaway => giveaway.entryMethod === filters.entryMethod);
    }

    // Apply prize value filter
    if (filters.prizeValue !== 'all') {
      filtered = filtered.filter(giveaway => {
        const value = parseInt(giveaway.prizeValue.replace(/[$,]/g, ''));
        switch (filters.prizeValue) {
          case 'under1000':
            return value < 1000;
          case '1000-2000':
            return value >= 1000 && value <= 2000;
          case 'over2000':
            return value > 2000;
          default:
            return true;
        }
      });
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(giveaway => giveaway.status === filters.status);
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        break;
      case 'value':
        filtered.sort((a, b) => {
          const valueA = parseInt(a.prizeValue.replace(/[$,]/g, ''));
          const valueB = parseInt(b.prizeValue.replace(/[$,]/g, ''));
          return valueB - valueA;
        });
        break;
      case 'participants':
        filtered.sort((a, b) => b.participants - a.participants);
        break;
      default: // ending
        filtered.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
        break;
    }

    setFilteredGiveaways(filtered);
  }, [giveaways, filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleParticipate = (giveaway) => {
    if (!isLoggedIn) {
      // Redirect to login or show login modal
      alert('Please log in to participate in giveaways');
      return;
    }

    setSelectedGiveaway(giveaway);
    setIsParticipationModalOpen(true);
  };

  const handleParticipationSubmit = async (participationData) => {
    try {
      const result = await giveawaysService.participateInGiveaway(
        selectedGiveaway.id,
        participationData
      );
      console.log('Participation successful:', result);
      setIsParticipationModalOpen(false);
      setSelectedGiveaway(null);
      // Reload user entries to show the new entry
      loadUserEntries();
    } catch (err) {
      console.error('Error participating in giveaway:', err);
      // You might want to show an error message to the user here
    }
  };

  const featuredGiveaways = filteredGiveaways.filter(g => g.featured);
  const regularGiveaways = filteredGiveaways.filter(g => !g.featured);

  if (showMyEntries) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <MyEntriesSection
              entries={mockUserEntries}
              onBack={() => setShowMyEntries(false)}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm">
      <Header />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-2xl flex items-center justify-center shadow-warm">
                  <Icon name="Gift" size={24} color="white" />
                </div>
                <div>
                  <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800">
                    Giveaways
                  </h1>
                  <p className="text-accent-600 mt-1">
                    Discover amazing prizes and enter to win exclusive giveaways
                  </p>
                </div>
              </div>

              {isLoggedIn && (
                <Button
                  variant="outline"
                  onClick={() => setShowMyEntries(true)}
                  iconName="User"
                  iconPosition="left"
                  className="hidden sm:flex"
                >
                  My Entries
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <GiveawayFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />

          {/* View Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-accent-600">
                {filteredGiveaways.length} active giveaways
              </span>
              {filteredGiveaways.length !== giveaways.length && (
                <button
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    search: '',
                    category: 'all',
                    entryMethod: 'all',
                    prizeValue: 'all'
                  }))}
                  className="text-sm text-primary hover:text-primary-600 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-accent-600 hover:bg-surface'
                  }`}
              >
                <Icon name="Grid3X3" size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-accent-600 hover:bg-surface'
                  }`}
              >
                <Icon name="List" size={18} />
              </button>
            </div>
          </div>

          {/* Featured Giveaways */}
          {featuredGiveaways.length > 0 && (
            <div className="mb-12">
              <h2 className="font-heading font-semibold text-xl text-accent-800 mb-6 flex items-center">
                <Icon name="Star" size={20} className="text-primary mr-2" />
                Featured Giveaways
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredGiveaways.map((giveaway) => (
                  <GiveawayCard
                    key={giveaway.id}
                    giveaway={giveaway}
                    onParticipate={() => handleParticipate(giveaway)}
                    isLoggedIn={isLoggedIn}
                    isFeatured={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Giveaways */}
          {regularGiveaways.length > 0 ? (
            <div>
              <h2 className="font-heading font-semibold text-xl text-accent-800 mb-6">
                All Giveaways
              </h2>
              <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
                }`}>
                {regularGiveaways.map((giveaway) => (
                  <GiveawayCard
                    key={giveaway.id}
                    giveaway={giveaway}
                    onParticipate={() => handleParticipate(giveaway)}
                    isLoggedIn={isLoggedIn}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Gift" size={32} className="text-accent-400" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-accent-800 mb-2">
                No giveaways match your filters
              </h3>
              <p className="text-accent-600 mb-6 max-w-md mx-auto">
                Try adjusting your search criteria or clearing filters to see more results.
              </p>
              <Button
                variant="primary"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  search: '',
                  category: 'all',
                  entryMethod: 'all',
                  prizeValue: 'all'
                }))}
                iconName="RotateCcw"
                iconPosition="left"
                className="shadow-warm"
              >
                Clear All Filters
              </Button>
            </div>
          )}

          {/* Mobile My Entries Button */}
          {isLoggedIn && (
            <div className="fixed bottom-6 right-6 sm:hidden">
              <Button
                variant="primary"
                onClick={() => setShowMyEntries(true)}
                iconName="User"
                shape="circle"
                className="shadow-lg"
              />
            </div>
          )}
        </div>
      </main>

      {/* Participation Modal */}
      <ParticipationModal
        isOpen={isParticipationModalOpen}
        onClose={() => {
          setIsParticipationModalOpen(false);
          setSelectedGiveaway(null);
        }}
        giveaway={selectedGiveaway}
        onSubmit={handleParticipationSubmit}
      />
    </div>
  );
};

export default GiveawaysPage;